
import {ProjectElement}                    from '@/models/project/project.element';
import {Project}                           from '@/models/project';
import {Logger}                            from "@/util/logger";
import {BatchSummaryDialogParams}          from "@/gui/dialogs/batch.summary.dialog.params";
import {empty}                             from "@/util/util";
import {BatchGroupElement}                 from "@/models/project/batch.group.element";
import {BlobType}                          from "@/models/blob";
import {ProjectElementFactory}             from "@/models/project.element.factory";
import {ProjectElementType}                from "@/models/project/project.element.type";
import {BatchMeasureElement}               from "@/models/project/batch.measure.element";
import {TaskProgressService}               from "@/services/task.progress.service";
import {TaskUpdate}                        from "@/models/task.update"
import {TaskBatchMeasureCreate}            from "@/models/task.batch.measure.create";
import {TaskBatchMeasureCreateComplete}    from "@/models/task.batch.measure.create.complete"
import {TaskBatchMeasureCreateFailed}      from "@/models/task.batch.measure.create.failed";

import {first}                             from "rxjs/operators";
import { v4 as uuidv4 }                    from 'uuid';

/**
 *
 * @class BatchSummaryCreator
 *
 * helper to take the parameters from a batch summary creation dialog and do
 * all the necessary work to actually create the summary and then add it as a leaf to
 * the Operational Measures > Batch Summaries branch in the user's current project.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2022
 *
 */
export class BatchSummaryCreator {

  /** the user's current project */
  private _project: Project;

  /** standard constructor */
  constructor(
    project: Project,
    kpi: string = '',
    bob: BatchGroupElement = null
  ) {

    this._project                = project;
    let bsc: BatchSummaryCreator = this;

    Logger.debug(`[BatchSummaryCreator] adding new batch summary, kpi=${kpi}...`);

    /* go through a dialog to get the name and parameters for the model */

    bsc._project.services.modals.open(
      'new-batch-summary',
      (value: BatchSummaryDialogParams, resolve, reject) => {

        let err: string = bsc.validate(value);

        if (err != '') {

          reject(err);
        }

        resolve(value);
      },
      (value: BatchSummaryDialogParams) => {

        bsc.create(value);

      },
      {
        kpi,
        project,
        bob
      });

  }

  /** the dialog parameters were accepted, time to create a model! */
  create(value: BatchSummaryDialogParams) {

    const bsc: BatchSummaryCreator = this;

    Logger.debug('[BatchSummaryCreator] create: ', value);

    /* we expect the name, kpiBlob (the blob key) and regressions to be filled in */

    if (empty(value.kpiBlob)) {

      Logger.error(`[BatchSummaryCreator] no rollup!`);

      return
    }

    if (value.regressions.length == 0) {

      Logger.error(`[BatchSummaryCreator] no regressions!`);

      return ;
    }

    /* track the start of the work... */

    const tasks: TaskProgressService = bsc._project.services.taskMonitor;
    const taskId: string             = TaskUpdate.generateId();

    tasks.progress(new TaskBatchMeasureCreate(taskId, `Batch measure (might take a few minutes): ${value.name} ...`, 1.0));

    /* use the kpi blob, and the regression blobs to get the final CSV data... */

    bsc._project.services.swe.batchSummarize(value.kpiBlob, value.regressions).then(
      (result: any) => {

        Logger.debug('[BatchSummaryCreator] creating batch measure:', result);

        tasks.progress(new TaskBatchMeasureCreate(taskId, `Formatting data...`, 50.0));

        /*
         * the result we get back is a JSON object with a dictionary of columns.  We
         * can save that as the JSON blob, but we also want CSV data.
         *
         *   HISTORY_MRT_R2 => {
         *     [0] => 0.9325481104
         *     [1] => 0.9325481104
         *     ...
         *
         * Generate the  CSV data...
         *
         */

        const csvLines: string [] = [];
        const keys: string[]      = Object.keys(result).sort();
        const firstKey: string    = keys[0]
        const N: number           = result[firstKey].length;

        /* header row */

        csvLines.push(keys.join(','));

        Logger.debug('[BatchSummaryCreator] header: ', csvLines[0]);

        /* we assume all columns are given with the same number of rows */

        Logger.debug('[BatchSummaryCreator] expected cols: ', keys.length);
        Logger.debug('[BatchSummaryCreator] expected rows: ', N);

        /* walk the row data ... */

        for (let i=0; i<N; i++) {

          csvLines.push(keys.map(x => result[x][i]).join(','));
        }

        /* add the new batch measure node to the project tree */

        Logger.debug('[BatchSummaryCreator] csv data: ', csvLines);

        const jsonData: any   = result;
        const csvData: string = csvLines.join("\n");

        /* place the blobs ... */

        const uuid: string           = uuidv4();
        const  projectFolder: string = bsc._project.dirname();
        const folder: string         = projectFolder + `/batch-summaries/${uuid}`;
        const jsonDataBlob: string   = `${folder}/${value.name}-batch-measure.json`;
        const csvDataBlob: string    = `${folder}/${value.name}-batch-measure.csv`;

        /* save the JSON ... */

        bsc._project.services.blobber.create(jsonDataBlob, BlobType.Json, JSON.stringify(jsonData), 'batch measure (json)', '')
          .pipe(first())
          .subscribe(
            created => {

              Logger.debug(`[BatchSummaryCreator] JSON saved ${jsonDataBlob}`);

              tasks.progress(new TaskBatchMeasureCreate(taskId, `JSON saved...`, 80.0));

              /* save the CSV ... */

              bsc._project.services.blobber.create(csvDataBlob, BlobType.CSV, csvData, 'batch measure (csv)', '')
                .pipe(first())
                .subscribe(
                  created => {

                    Logger.debug(`[BatchSummaryCreator] CSV saved ${csvDataBlob}`);

                    tasks.progress(new TaskBatchMeasureCreate(taskId, `CSV saved...`, 80.0));

                    const group: ProjectElement = bsc._project.batchSummaries();

                    const el: BatchMeasureElement = ProjectElementFactory.fromObject(
                      ProjectElementType.BatchMeasure,
                      {
                        project: this._project,
                        id:      '',
                        input:   jsonDataBlob,
                        input2:  csvDataBlob,
                        name:    value.name,
                        parent:  group,
                        publish: true
                      }) as BatchMeasureElement;

                    bsc._project.expand([group]);

                    bsc._project.expand([el]);

                    el.save();

                    Logger.debug('[BatchSummaryCreator] COMPLETE!');

                    /* track the success */

                    tasks.progress(new TaskBatchMeasureCreateComplete(taskId, `[${value.name}] Measure ready!`));

                  },
                  (error) => {

                    Logger.error('[BatchSummaryCreator] could not save CSV final data: ', error);

                    /* track the failure */

                    tasks.progress(new TaskBatchMeasureCreateFailed(taskId, error));

                  });
            },
            (error) => {

              Logger.error('[BatchSummaryCreator] could not save JSON final data: ', error);

              /* track the failure */

              tasks.progress(new TaskBatchMeasureCreateFailed(taskId, error));

            });
      },
      (error) => {

        /* log it */

        Logger.error('[BatchSummaryCreator] could not calculate final CSV data: ', error);

        /* track the failure */

        tasks.progress(new TaskBatchMeasureCreateFailed(taskId, error));

      });

    return ;
  }

  /** validate the parameters we got from the dialog */
  validate(value: BatchSummaryDialogParams): string {

      let bsc: BatchSummaryCreator = this;
      let parent: ProjectElement   = bsc._project.batchSummaries();
      let name: string             = value.name;

      Logger.debug(`[BatchSummaryCreator] (new batch summary) validating, name=${name} ...`);

      /* is it a valid name? */

      if (name.trim() === '') {

        return `Batch Summary 'Name' can not be empty.`;
      }

      /* do we already have a model of this name? */

      const peers: ProjectElement[] = parent._children;

      for (const peer of peers) {


        if (peer._label === name) {

          return 'Batch Measure of that name exists already.';
        }

      }

      if (!value.kpi && !value.bob) {

        return 'A KPI Rollup must be provided';
      }

      if (!value.regressions || (value.regressions.length == 0)) {

        return 'At least one regression is required.';
      }

      /* its golden! */

      value.name = name;

      return '';
    }

}
